import React, { useRef, useState, useCallback } from 'react';

import Link from 'next/link';
import { MdArrowBackIosNew, MdArrowForwardIos } from 'react-icons/md';
import { SlBadge } from 'react-icons/sl';
import { useWindowSize } from 'usehooks-ts';

import { User } from '@/models/user';

const RecommendedShops = ({
  showRoomsShopsData,
  title,
}: {
  showRoomsShopsData: User[];
  title: string;
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showPrevButton, setShowPrevButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);
  const { width: windowWidth } = useWindowSize();

  const checkScrollButtons = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const canScroll = scrollWidth > clientWidth;
      const isEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setShowPrevButton(scrollLeft > 0 && canScroll);
      setShowNextButton(!isEnd && canScroll);
    }
  }, []);

  React.useEffect(() => {
    checkScrollButtons();
  }, [showRoomsShopsData, checkScrollButtons]);

  const handlePrevClick = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  }, []);

  const handleNextClick = useCallback(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const isEnd = scrollLeft + clientWidth >= scrollWidth - 1; // Add small tolerance
      const canScroll = scrollWidth > clientWidth;

      setShowPrevButton(scrollLeft > 0 && canScroll);
      setShowNextButton(!isEnd && canScroll);
    }
  }, []);

  const id = showRoomsShopsData.map((user) => user.kcid);
  const fullname = showRoomsShopsData.map((user) => user.fullname);
  const users = showRoomsShopsData.map((user) => user);

  const track = (event: string) => {
    // @ts-ignore
    if (window.rudderanalytics?.track) {
      window.rudderanalytics.track(event, {
        id,
        fullname,
      });
    }
  };

  return (
    <>
      <div className="my-2 lg:my-6 ml-4 lg:mx-auto">
        <h3 className="font-extrabold text-2xl lg:text-2xl tracking-wide text-neutral-800 flex gap-x-3 items-center my-8">
          <span className="p-[6px] bg-primary text-white rounded-full font-bold">
            <SlBadge size={20} />
          </span>
          <span className="text-lg lg:text-xl">{title}</span>
        </h3>
        <div className="w-full mx-auto overflow-hidden relative flex justify-center">
          <div
            ref={carouselRef}
            className={`flex lg:gap-x-2 my-2 overflow-x-auto scrollbar-none scroll-smooth ${
              windowWidth >= 1500 && users.length <= 5
                ? 'justify-center'
                : 'justify-start'
            }`}
            onScroll={handleScroll}
            onLoad={checkScrollButtons}
          >
            {showRoomsShopsData.map((user, index) => (
              <div
                className="flex flex-col min-w-[9rem] items-center lg:min-w-[10rem]"
                key={index}
                onClick={() => {
                  track('clicked_recommended_shop');
                }}
              >
                <div className="relative flex justify-center">
                  <Link href={`/shop/${user.kcid}`}>
                    <img
                      className="w-[4rem] h-[4rem] lg:w-[6rem] lg:h-[6rem] shadow-md rounded-full border-[1px] border-gray-700 hover:shadow-none hover:border-primary"
                      src={
                        // @ts-ignore
                        user?.avatar.includes('http')
                          ? `https://www.tayara.tn/mediaGateway/resize-image?img=${
                              // @ts-ignore
                              user?.avatar.substring(
                                // @ts-ignore
                                user?.avatar.lastIndexOf('/') - 2
                              )
                            }&w=300`
                          : `/media/t-logo-primary.png`
                      }
                      alt={`Profile picture of ${user.username}`}
                    />
                  </Link>
                </div>
                <p className="text-center my-4 font-bold text-sm lg:text-sm capitalize w-[10rem]">
                  {user.fullname}
                </p>
              </div>
            ))}
          </div>
          {(windowWidth < 1500 || users.length > 5) && (
            <>
              <div className="absolute top-1/2 -right-1 transform -translate-y-1/2 px-8 md:px-10 h-[160px] bg-gradient-to-l from-neutral-50 z-40">
                {showNextButton && (
                  <button
                    className="absolute top-[34%] transform -translate-y-1/2 right-1 bg-white border border-neutral-400 p-2 md:p-4 rounded-full text-neutral-700 hover:text-neutral-900 focus:outline-none focus:bg-neutral-110 z-50"
                    onClick={handleNextClick}
                  >
                    <MdArrowForwardIos size={20} />
                  </button>
                )}
              </div>
              <div className="absolute top-1/2 -left-1 transform -translate-y-1/2 px-8 md:px-4 h-[160px] bg-gradient-to-r from-neutral-50 z-40">
                {showPrevButton && (
                  <button
                    className="absolute top-[34%] transform -translate-y-1/2 left-2 bg-white border border-neutral-400 p-2 md:p-4 rounded-full text-neutral-700 hover:text-neutral-900 focus:outline-none focus:bg-neutral-110 z-50"
                    onClick={handlePrevClick}
                  >
                    <MdArrowBackIosNew size={20} />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default RecommendedShops;
